import type { EnumItem, EnumType, EnumValue } from '@/types/pt_types'

export function getValue<T>(model: any, key: string): T {
  return model[key] as T
}

export function setValue(model: any, key: string, value: any): void {
  model[key] = value
}

export function getValueByPath<T>(model: any, path: string): T | undefined {
  const segments = path.split('/')
  let currentModel: any = model
  for (const segment of segments) {
    if (!segment) {
      continue
    }
    currentModel = currentModel[segment]
    if (currentModel === undefined || currentModel === null) {
      return undefined
    }
  }
  return currentModel as T
}

export function setValueByPath(model: any, path: string, value: any): void {
  const segments = path.split('/').filter((segment) => !!segment)
  let currentModel: any = model
  while (segments.length > 1) {
    const segment = segments.pop() as string
    currentModel = currentModel[segment] || {}
  }
  currentModel[segments.pop() as string] = value
}

// 只复制变化的值
export function copyModelWithFilter(source: any, filter: any) {
  const result = {}
  copyModel(source, result, (key) => {
    if (source[key] !== undefined && source[key] !== null && source[key] !== filter[key]) {
      return key
    }
    return undefined
  })
  return result
}

export function copyModelExclude(source: any, target: any, ...excludeProperties: string[]) {
  copyModel(source, target, (key) => {
    if (excludeProperties.includes(key)) {
      return undefined
    }
    return key
  })
}

export function copyModelInclude(source: any, target: any, ...includeProperties: string[]) {
  copyModel(source, target, (key) => {
    if (includeProperties.includes(key)) {
      return key
    }
    return undefined
  })
}

export function copyModel(
  source: any,
  target: any,
  keyTransfer?: (key: string, value: any) => string | undefined,
  valueTransfer?: (key: string, value: any) => any | undefined
): void {
  scanModel(source, (key, value) => {
    const newKey = keyTransfer ? keyTransfer(key, value) : key
    if (newKey) {
      if (valueTransfer !== undefined) {
        target[newKey] = valueTransfer(key, value)
      } else {
        target[newKey] = value
      }
    }
  })
}

export function scanModel(source: any, handler: (name: string, value: any) => void) {
  // Object.entries(source).forEach(([key, value]) => handler(key, value))
  // 以下方法有时候遍历不出属性
  for (const sourceProperty in source) {
    handler(sourceProperty, source[sourceProperty])
  }
}

export function cleanModel(source: any, enableFun?: (name: string, value: any) => boolean) {
  const list: string[] = []
  for (const sourceProperty in source) {
    if (typeof sourceProperty === 'function') {
      continue
    }
    if (!enableFun || enableFun(sourceProperty, source[sourceProperty])) {
      list.push(sourceProperty)
    }
  }
  list.forEach((name) => delete source[name])
}

export function cleanAndFillModel(model: any, defaultValue?: any) {
  cleanModel(model)
  if (defaultValue) {
    copyModel(defaultValue, model)
  }
}

export function defaultModel(source: any) {
  if (Array.isArray(source)) {
    // source.splice(0)
    source = []
  } else {
    scanModel(source, (key, value) => {
      if (value === undefined || value === null) {
        return
      }
      const type = (typeof value).toLowerCase()
      if (type === 'number') {
        source[key] = 0
      } else if (type === 'string') {
        source[key] = ''
      }
      source[key] = undefined
    })
  }
}

export function enumFromValue<T extends EnumItem<V>, V extends EnumValue>(enumType: EnumType<T, V>, value: V) {
  return enumType.fromValue(value)
}

export function diff<T extends Record<string, any>>(newObj: T, oldObj: T) {
  const patch: Partial<T> = {}

  Object.keys(newObj).forEach((key) => {
    if (newObj[key] !== oldObj[key]) {
      if (typeof newObj[key] === 'object' && typeof oldObj[key] === 'object') {
        // 对象属性比较对应的JSON字符串, 如果有变更替换整个对象属性
        const newValue = JSON.stringify(newObj[key])
        const oldValue = JSON.stringify(oldObj[key])
        if (newValue === oldValue) {
          return
        }
      }
      patch[key as keyof T] = newObj[key]
    }
  })

  return patch
}
